import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { HelmetProvider } from 'react-helmet-async';
import Home from "./pages/Home";
import Login from "./pages/Login";
import LoginComplete from "./components/auth/LoginComplete";
import UserRegistration from "./components/auth/UserRegistration";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import ApprovedRoute from "./components/auth/ApprovedRoute";
import AdminRoute from "./components/auth/AdminRoute";
import PortalLayout from "./components/layout/PortalLayout";
import Dashboard from "./pages/portal/Dashboard";
import Profile from "./pages/portal/Profile";
import PortalEvents from "./pages/portal/Events";
import EventRegistration from "./pages/portal/EventRegistration";
import RegistrationSuccess from "./pages/portal/RegistrationSuccess";
import UserTickets from "./pages/portal/UserTickets";
import TicketDetail from "./pages/portal/TicketDetail";
import Resources from "./pages/portal/Resources";
import Notifications from "./pages/portal/Notifications";
import Feedback from "./pages/portal/Feedback";
import AdminPage from "./pages/admin";
import Shop from "./pages/Shop";
import NewsPage from "./pages/news";
import NewsDetail from "./pages/news/detail";
import PublicEvents from "./pages/Events";
import PublicRegistrationSuccess from "./pages/EventRegistrationSuccess";
import Athletes from "./pages/Athletes";
import Friends from "./pages/Friends";

// Create a custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#3b82f6", // Tailwind blue-500
    },
    secondary: {
      main: "#8b5cf6", // Tailwind purple-500
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
  },
});

// Component to check if user profile is complete
const ProfileRequiredRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, isProfileComplete, loading } = useAuth();

  if (loading) {
    return null; // or a loading spinner
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!isProfileComplete) {
    return <Navigate to="/register" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <HelmetProvider>
            <Router>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/login/complete" element={<LoginComplete />} />
                <Route path="/athletes" element={<Athletes />} />
                <Route path="/friends" element={<Friends />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/news/:slug" element={<NewsDetail />} />
                <Route path="/events" element={<PublicEvents />} />
                <Route
                  path="/events/registration/:eventId"
                  element={<EventRegistration />}
                />
                <Route
                  path="/events/registration-success"
                  element={<PublicRegistrationSuccess />}
                />

                <Route
                  path="/register"
                  element={
                    <ProtectedRoute>
                      <UserRegistration />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/portal"
                  element={
                    <ProtectedRoute>
                      <ProfileRequiredRoute>
                        <PortalLayout />
                      </ProfileRequiredRoute>
                    </ProtectedRoute>
                  }
                >
                  <Route
                    index
                    element={<Navigate to="/portal/dashboard" replace />}
                  />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="profile" element={<Profile />} />
                  <Route 
                    path="events" 
                    element={
                      <ApprovedRoute>
                        <PortalEvents />
                      </ApprovedRoute>
                    } 
                  />
                  <Route
                    path="events/registration/:eventId"
                    element={
                      <ApprovedRoute>
                        <EventRegistration />
                      </ApprovedRoute>
                    }
                  />
                  <Route
                    path="events/registration-success"
                    element={
                      <ApprovedRoute>
                        <RegistrationSuccess />
                      </ApprovedRoute>
                    }
                  />
                  <Route 
                    path="tickets" 
                    element={
                      <ApprovedRoute>
                        <UserTickets />
                      </ApprovedRoute>
                    } 
                  />
                  <Route 
                    path="tickets/:ticketId" 
                    element={
                      <ApprovedRoute>
                        <TicketDetail />
                      </ApprovedRoute>
                    } 
                  />
                  <Route 
                    path="resources" 
                    element={
                      <ApprovedRoute>
                        <Resources />
                      </ApprovedRoute>
                    } 
                  />
                  <Route path="notifications" element={<Notifications />} />
                  <Route 
                    path="feedback" 
                    element={
                      <ApprovedRoute>
                        <Feedback />
                      </ApprovedRoute>
                    } 
                  />

                  {/* Admin Route - Now nested inside portal */}
                  <Route
                    path="admin/*"
                    element={
                      <AdminRoute>
                        <AdminPage />
                      </AdminRoute>
                    }
                  />
                </Route>
              </Routes>
            </Router>
          </HelmetProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
